import React, { useState, useEffect } from 'react';
import './MonthlyJournalEntries.css';
import { apiCall } from '../config';

const MonthlyJournalEntries = () => {
  const [fgInventoryData, setFgInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthsLoaded, setMonthsLoaded] = useState(false);
  const [qboAccountMapping, setQboAccountMapping] = useState([]);
  const [journalPreview, setJournalPreview] = useState([]);
  const [isLoadingJournal, setIsLoadingJournal] = useState(false);
  const [isSubmittingToQBO, setIsSubmittingToQBO] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [journalError, setJournalError] = useState(null);
  
  // New state for component inventory breakdown
  const [componentInventoryData, setComponentInventoryData] = useState([]);
  const [componentInventorySummary, setComponentInventorySummary] = useState({});
  const [isLoadingComponentInventory, setIsLoadingComponentInventory] = useState(false);
  const [componentInventoryError, setComponentInventoryError] = useState(null);

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    if (availableMonths.length > 0 && selectedMonth && monthsLoaded) {
      fetchFgInventoryData();
      fetchComponentInventoryData();
    }
  }, [selectedMonth, availableMonths, monthsLoaded]);

  // Fetch QBO account mapping on mount
  useEffect(() => {
    const fetchQboMapping = async () => {
      try {
        const response = await apiCall('/api/monthly-journal-entries/qbo-account-mapping', { method: 'GET' });
        if (response.ok) {
          const data = await response.json();
          setQboAccountMapping(data);
        }
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchQboMapping();
  }, []);

  // Generate journal preview when FG data or mapping changes
  useEffect(() => {
    if (!fgInventoryData.length || !qboAccountMapping.length) {
      setJournalPreview([]);
      return;
    }
    
    // Fetch journal preview from backend
    const fetchJournalPreview = async () => {
      try {
        setIsLoadingJournal(true);
        setJournalError(null);
        
        const url = selectedMonth 
          ? `/api/monthly-journal-entries/journal-preview?month=${selectedMonth}`
          : '/api/monthly-journal-entries/journal-preview';
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.success) {
          // Transform the data to match the expected format
          const rows = data.journal_rows.map(row => ({
            row_number: row.row_number,
            account: `${row.account_name} (${row.account_id})`,
            account_id: row.account_id,
            description: row.description,
            type: row.type,
            amount: row.amount
          }));
          
          setJournalPreview(rows);
        } else {
          setJournalError(data.error || 'Failed to generate journal preview');
        }
      } catch (err) {
        setJournalError('Network error: ' + err.message);
      } finally {
        setIsLoadingJournal(false);
      }
    };
    
    fetchJournalPreview();
  }, [fgInventoryData, qboAccountMapping, selectedMonth]);

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch('/api/monthly-journal-entries/available-months', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setAvailableMonths(data.months);
        // Set the most recent month as default
        if (data.months.length > 0) {
          setSelectedMonth(data.months[0].value);
        }
        setMonthsLoaded(true);
      }
    } catch (err) {
      console.error('Error fetching available months:', err);
    }
  };

  const fetchFgInventoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = selectedMonth 
        ? `/api/monthly-journal-entries/fg-inventory-data?month=${selectedMonth}`
        : '/api/monthly-journal-entries/fg-inventory-data';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setFgInventoryData(data.data);
        setTotalAmount(data.total_amount);
        setTotalItems(data.total_items);
      } else {
        setError(data.error || 'Failed to fetch FG inventory data');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchComponentInventoryData = async () => {
    try {
      setIsLoadingComponentInventory(true);
      setComponentInventoryError(null);
      
      const url = selectedMonth 
        ? `/api/monthly-journal-entries/component-inventory-data?month=${selectedMonth}`
        : '/api/monthly-journal-entries/component-inventory-data';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setComponentInventoryData(data.data);
        setComponentInventorySummary(data.summary);
      } else {
        setComponentInventoryError(data.error || 'Failed to fetch component inventory data');
      }
    } catch (err) {
      setComponentInventoryError('Network error: ' + err.message);
    } finally {
      setIsLoadingComponentInventory(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const submitToQBO = async () => {
    try {
      setIsSubmittingToQBO(true);
      setJournalError(null);
      setSuccessMessage(null);
      
      const payload = {
        journal_entry: journalPreview.map(row => ({
          account_id: row.account_id,
          description: row.description,
          type: row.type,
          amount: row.amount
        })),
        month: selectedMonth
      };
      
      const response = await fetch('/api/monthly-journal-entries/submit-to-qbo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'Journal entry posted to QBO successfully!');
        alert(`✅ ${data.message || 'Journal entry posted to QBO successfully!'}\n\nQBO ID: ${data.qbo_id}\nTransaction Date: ${data.transaction_date}\nLines: ${data.lines_count}\nTotal Amount: ${formatCurrency(data.total_amount)}`);
      } else {
        const errorData = await response.json();
        setJournalError(errorData.error || 'Failed to submit journal entry to QBO');
        alert(`❌ ${errorData.error || 'Failed to submit journal entry to QBO'}`);
      }
    } catch (err) {
      setJournalError('Network error occurred while submitting to QBO');
      alert('❌ Network error occurred while submitting to QBO');
    } finally {
      setIsSubmittingToQBO(false);
    }
  };

  if (loading || !monthsLoaded) {
    return (
      <div className="sundeep-invoice-process">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{!monthsLoaded ? 'Loading available months...' : 'Loading FG inventory data...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="monthly-journal-entries">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchFgInventoryData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sundeep-invoice-process">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Monthly JE - Finished Goods Creation</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Calculate and prepare journal entries for QBO export</li>
              <li>Filter by month to see specific period data</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>
      <div className="page-content">
        {/* Month Filter - Moved to top */}
        <div className="section">
          <div className="filter-controls">
            <div className="month-filter">
              <label htmlFor="month-select">Select Month:</label>
              <select 
                id="month-select"
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={loading}
              >
                <option value="">All Months</option>
                {availableMonths.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Journal Entry Preview Section - Moved to top */}
        <div className="section">
          <h2>📝 Journal Entry Preview - Finished Goods Creation</h2>
          {isLoadingJournal ? (
            <div className="loading">Loading journal entry preview...</div>
          ) : journalError ? (
            <div className="error">❌ {journalError}</div>
          ) : successMessage ? (
            <div className="success">✅ {successMessage}</div>
          ) : journalPreview.length > 0 ? (
            <div className="journal-entry-container">
              <div className="journal-entry-summary">
                <h3>Journal Entry Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item">
                    <strong>Total Debits:</strong> {formatCurrency(journalPreview.filter(row => row.type === 'DEBIT').reduce((sum, row) => sum + row.amount, 0))}
                  </div>
                  <div className="stat-item">
                    <strong>Total Credits:</strong> {formatCurrency(journalPreview.filter(row => row.type === 'CREDIT').reduce((sum, row) => sum + row.amount, 0))}
                  </div>
                  <div className="stat-item">
                    <strong>Rows:</strong> {journalPreview.length}
                  </div>
                </div>
              </div>
              <table className="journal-entry-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Account</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {journalPreview.map((row) => (
                    <tr key={row.row_number} className={`journal-row ${row.type.toLowerCase()}`}> 
                      <td className="row-number">{row.row_number}</td>
                      <td className="account">{row.account}</td>
                      <td className="description">{row.description}</td>
                      <td className="type">
                        <span className={`type-badge ${row.type.toLowerCase()}`}>{row.type.toUpperCase()}</span>
                      </td>
                      <td className="amount">{formatCurrency(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="journal-entry-actions">
                <button 
                  className="btn btn-primary"
                  onClick={submitToQBO}
                  disabled={isSubmittingToQBO || journalPreview.length === 0}
                >
                  {isSubmittingToQBO ? '⏳ Submitting...' : '📤 Submit to QuickBooks Online'}
                </button>
              </div>
            </div>
          ) : (
            <div className="no-data">No journal entry data available for this month.</div>
          )}
        </div>

        <div className="section">
          <h2>Finished Goods Inventory</h2>
          <p>Inventory to be added based on Sundeep invoice data</p>
          
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Items:</span>
              <span className="stat-value">{formatNumber(totalItems)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Amount:</span>
              <span className="stat-value">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
          
          {fgInventoryData.length === 0 ? (
            <div className="no-data">
              <p>No FG inventory data found. Please check if Sundeep invoice data has been imported.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>IGD SKU Number</th>
                    <th>IGD Name</th>
                    <th>Quantity</th>
                    <th>Cost per Unit</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {fgInventoryData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.igd_sku_number}</td>
                      <td>{item.igd_name}</td>
                      <td>{formatNumber(item.quantity)}</td>
                      <td>{formatCurrency(item.cost_per_unit)}</td>
                      <td className="amount-cell">{formatCurrency(item.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Component Inventory Breakdown Section */}
        <div className="section">
          <h2>🔧 Component Inventory Breakdown</h2>
          <p>Detailed component usage and costs based on BOM structure</p>
          
          {isLoadingComponentInventory ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading component inventory breakdown...</p>
            </div>
          ) : componentInventoryError ? (
            <div className="error-container">
              <h3>Error</h3>
              <p>{componentInventoryError}</p>
              <button onClick={fetchComponentInventoryData} className="retry-button">
                Retry
              </button>
            </div>
          ) : componentInventoryData.length === 0 ? (
            <div className="no-data">
              <p>No component inventory data found. Please check if BOM structure and component costs are configured.</p>
            </div>
          ) : (
            <div className="component-inventory-container">
              {/* Summary Section */}
              <div className="component-summary">
                <h3>Component Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Components:</span>
                    <span className="stat-value">{componentInventorySummary.total_components || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Value:</span>
                    <span className="stat-value">{formatCurrency(componentInventorySummary.total_value || 0)}</span>
                  </div>
                </div>
                
                {/* BOM Category Summary */}
                {componentInventorySummary.bom_categories && Object.keys(componentInventorySummary.bom_categories).length > 0 && (
                  <div className="bom-category-summary">
                    <h4>BOM Category Totals</h4>
                    <div className="category-totals">
                      {Object.entries(componentInventorySummary.bom_categories).map(([category, total]) => (
                        <div key={category} className={`category-item${category === 'Uncategorized' ? ' uncategorized' : ''}`}>
                          <span className="category-name">{category}:</span>
                          <span className="category-amount">{formatCurrency(total)}</span>
                        </div>
                      ))}
                      {/* Explicitly show uncategorized if present and not in the list */}
                      {componentInventorySummary.uncategorized_total > 0 && !componentInventorySummary.bom_categories?.Uncategorized && (
                        <div className="category-item uncategorized">
                          <span className="category-name">Uncategorized:</span>
                          <span className="category-amount">{formatCurrency(componentInventorySummary.uncategorized_total)}</span>
                        </div>
                      )}
                    </div>
                    {componentInventorySummary.uncategorized_total > 0 && (
                      <div className="uncategorized-warning">
                        <span>⚠️ Some components are missing a BOM category and are shown as <b>Uncategorized</b>.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Component Totals Table */}
              {componentInventorySummary.component_totals && Object.keys(componentInventorySummary.component_totals).length > 0 && (
                <div className="component-totals-section">
                  <h3>Component Totals</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Component ID</th>
                          <th>BOM Category</th>
                          <th>Total Quantity</th>
                          <th>Cost per Unit</th>
                          <th>Total Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(componentInventorySummary.component_totals).map(([component_id, data]) => (
                          <tr key={component_id} className={data.bom_category === 'Uncategorized' ? 'uncategorized-row' : ''}>
                            <td>{component_id}</td>
                            <td>{data.bom_category}</td>
                            <td>{formatNumber(data.total_quantity)}</td>
                            <td>{formatCurrency(data.cost_per_unit)}</td>
                            <td className="amount-cell">{formatCurrency(data.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detailed Breakdown Table */}
              <div className="detailed-breakdown-section">
                <h3>Detailed Breakdown by SKU and Component</h3>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>IGD SKU Number</th>
                        <th>SKU Quantity</th>
                        <th>Component ID</th>
                        <th>Component Qty/Unit</th>
                        <th>Total Component Qty</th>
                        <th>Cost per Unit</th>
                        <th>Total Cost</th>
                        <th>BOM Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {componentInventoryData.map((item, index) => (
                        <tr key={index} className={item.bom_category === 'Uncategorized' ? 'uncategorized-row' : ''}>
                          <td>{item.igd_sku_number}</td>
                          <td>{formatNumber(item.igd_sku_quantity)}</td>
                          <td>{item.component_id}</td>
                          <td>{formatNumber(item.component_quantity_per_unit)}</td>
                          <td>{formatNumber(item.total_component_quantity)}</td>
                          <td>{formatCurrency(item.cost_per_unit)}</td>
                          <td className="amount-cell">{formatCurrency(item.total_cost)}</td>
                          <td>{item.bom_category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="actions">
          <button 
            onClick={fetchFgInventoryData} 
            className="refresh-button"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyJournalEntries; 